
// src/pages/Inspection/Inspection.jsx

import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import {
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';

const initialForm = {
  pre_inspection_date: '',
  post_inspection_date: '',
  work_order_no: '',
  remark: '',
};



const formatDate = (value) => {
  if (!value) return '—';

  const date = String(value).slice(0, 10);
  const parts = date.split('-');

  if (parts.length !== 3) return date;

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const Inspection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
const [activeTab, setActiveTab] = useState('ALL');

const [summary, setSummary] = useState({
  all_count: 0,
  pending_count: 0,
  partial_count: 0,
  completed_count: 0,
});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);

  const [alert, setAlert] = useState({
    type: '',
    message: '',
  });


  const handleTabChange = (event, newValue) => {
  setActiveTab(newValue);
  setPage(0);
};


 const fetchInspectionList = useCallback(async () => {
  setLoading(true);

  try {
    const token = localStorage.getItem('token');

    const response = await api.get('/inspection', {
      params: {
        page: page + 1,
        limit,
        search: search.trim(),
        status: activeTab,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const result = response.data;

    setRows(result?.data || []);
    setTotal(Number(result?.pagination?.total || 0));

    setSummary(
      result?.summary || {
        all_count: 0,
        pending_count: 0,
        partial_count: 0,
        completed_count: 0,
      }
    );
  } catch (error) {
    console.error('Inspection list error:', error);

    setAlert({
      type: 'error',
      message:
        error.response?.data?.message ||
        'Failed to load inspection list',
    });
  } finally {
    setLoading(false);
  }
}, [page, limit, search, activeTab]); 

  useEffect(() => {
    fetchInspectionList();
  }, [fetchInspectionList]);

  const openDialog = (row) => {
    setSelected(row);

    setForm({
      pre_inspection_date:
        row.pre_inspection_date?.toString().slice(0, 10) || '',
      post_inspection_date:
        row.post_inspection_date?.toString().slice(0, 10) || '',
      work_order_no: row.work_order_no || '',
      remark: row.remark || '',
    });

    setAlert({ type: '', message: '' });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;

    setDialogOpen(false);
    setSelected(null);
    setForm(initialForm);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!selected?.visit_id) return;

    setSaving(true);
    setAlert({ type: '', message: '' });

    try {
      const token = localStorage.getItem('token');

      await api.put(
        `/inspection/${selected.visit_id}`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDialogOpen(false);
      setSelected(null);
      setForm(initialForm);

      setAlert({
        type: 'success',
        message: 'Inspection details saved successfully',
      });

      fetchInspectionList();
    } catch (error) {
      console.error('Save inspection error:', error);

      setAlert({
        type: 'error',
        message:
          error.response?.data?.message ||
          'Failed to save inspection details',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const renderInspectionStatus = (row) => {
  const hasPre = Boolean(row.pre_inspection_date);
  const hasPost = Boolean(row.post_inspection_date);
  const hasWorkOrder = Boolean(row.work_order_no?.trim());



  
  if (hasPre && hasPost && hasWorkOrder) {
    return <Chip label="Completed" color="success" size="small" />;
  }

  if (hasPre || hasPost || hasWorkOrder) {
    return <Chip label="Partially Completed" color="warning" size="small" />;
  }

  return <Chip label="Pending" variant="outlined" size="small" />;
};

  const renderActions = (row) => (
    <Tooltip title="Enter / Edit Inspection">
      <IconButton
        color="primary"
        onClick={() => openDialog(row)}
        size="small"
      >
        <EditIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%' }}>
      {/* HEADER */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <FactCheckIcon color="primary" sx={{ fontSize: 30 }} />

          <Box>
            <Typography variant="h5" fontWeight={700}>
              Coach Inspection
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Inspection details for coaches currently in the yard
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchInspectionList}
          disabled={loading}
          fullWidth={isMobile}
        >
          Refresh
        </Button>
      </Stack>

      {/* ALERT */}
      {alert.message && (
        <Alert
          severity={alert.type || 'info'}
          onClose={() => setAlert({ type: '', message: '' })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* SUMMARY */}
 
<Paper
  variant="outlined"
  sx={{
    mb: 2,
    width: '100%',
    overflow: 'hidden',
    borderRadius: 2,
  }}
>
  <Tabs
    value={activeTab}
    onChange={handleTabChange}
    variant={isMobile ? 'fullWidth' : 'scrollable'}
    scrollButtons={isMobile ? false : 'auto'}
    allowScrollButtonsMobile
    sx={{
      minHeight: { xs: 48, sm: 60 },
      px: { xs: 0, sm: 1 },

      '& .MuiTab-root': {
        minHeight: { xs: 48, sm: 60 },
        minWidth: { xs: 0, sm: 125 },
        px: { xs: 0.5, sm: 2 },
        py: 1,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: { xs: '0.72rem', sm: '0.875rem' },
        whiteSpace: 'nowrap',
      },

      '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    }}
  >
    <Tab
      value="ALL"
      label={
        <Stack
          direction="row"
          spacing={{ xs: 0.4, sm: 1 }}
          alignItems="center"
          justifyContent="center"
        >
          <span>All</span>
          <Chip
            size="small"
            label={summary.all_count}
            variant="outlined"
            sx={{
              height: { xs: 19, sm: 22 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              '& .MuiChip-label': {
                px: { xs: 0.6, sm: 1 },
              },
            }}
          />
        </Stack>
      }
    />

    <Tab
      value="PENDING"
      label={
        <Stack
          direction="row"
          spacing={{ xs: 0.4, sm: 1 }}
          alignItems="center"
          justifyContent="center"
        >
          <span>Pending</span>
          <Chip
            size="small"
            label={summary.pending_count}
            color="default"
            sx={{
              height: { xs: 19, sm: 22 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              '& .MuiChip-label': {
                px: { xs: 0.6, sm: 1 },
              },
            }}
          />
        </Stack>
      }
    />

    <Tab
      value="PARTIAL"
      label={
        <Stack
          direction="row"
          spacing={{ xs: 0.4, sm: 1 }}
          alignItems="center"
          justifyContent="center"
        >
          <span>Partial</span>
          <Chip
            size="small"
            label={summary.partial_count}
            color="warning"
            sx={{
              height: { xs: 19, sm: 22 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              '& .MuiChip-label': {
                px: { xs: 0.6, sm: 1 },
              },
            }}
          />
        </Stack>
      }
    />

    <Tab
      value="COMPLETED"
      label={
        <Stack
          direction="row"
          spacing={{ xs: 0.4, sm: 1 }}
          alignItems="center"
          justifyContent="center"
        >
          <span>Completed</span>
          <Chip
            size="small"
            label={summary.completed_count}
            color="success"
            sx={{
              height: { xs: 19, sm: 22 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              '& .MuiChip-label': {
                px: { xs: 0.6, sm: 1 },
              },
            }}
          />
        </Stack>
      }
    />
  </Tabs>
</Paper>



      {/* SEARCH */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={handleSearch}
          placeholder="Search coach number, visit number or work order..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearch('');
                    setPage(0);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Paper>

      {/* LIST */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box
            sx={{
              minHeight: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No coaches currently found in the yard.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Stack spacing={1} sx={{ p: 1 }}>
            {rows.map((row) => (
              <Card
                key={row.visit_id}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700} variant="subtitle1">
                        {row.coach_no || '—'}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Visit: {row.visit_no || '—'}
                      </Typography>
                    </Box>

                    {renderActions(row)}
                  </Stack>

                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Coach Type
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {row.coach_type_code || '—'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Railway / Base
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {[row.railway_code, row.base_code]
                          .filter(Boolean)
                          .join(' / ') || '—'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Pre-inspection
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(row.pre_inspection_date)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Post-inspection
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(row.post_inspection_date)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        {renderInspectionStatus(row)}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: 'right' }}
                        >
                          {row.work_order_no || 'No work order'}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 950 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell>Coach No.</TableCell>
                  <TableCell>Visit No.</TableCell>
                  <TableCell>Coach Type</TableCell>
                  <TableCell>Railway / Base</TableCell>
                  <TableCell>Pre-Inspection</TableCell>
                  <TableCell>Post-Inspection</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.visit_id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>
                        {row.coach_no || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>{row.visit_no || '—'}</TableCell>
                    <TableCell>{row.coach_type_code || '—'}</TableCell>
                    <TableCell>
                      {[row.railway_code, row.base_code]
                        .filter(Boolean)
                        .join(' / ') || '—'}
                    </TableCell>
                    <TableCell>
                      {formatDate(row.pre_inspection_date)}
                    </TableCell>
                    <TableCell>
                      {formatDate(row.post_inspection_date)}
                    </TableCell>
                    <TableCell>{row.work_order_no || '—'}</TableCell>
                    <TableCell>{renderInspectionStatus(row)}</TableCell>
                    <TableCell align="center">
                      {renderActions(row)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={limit}
          onRowsPerPageChange={(event) => {
            setLimit(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={isMobile ? 'Rows' : 'Rows per page'}
        />
      </Paper>

      {/* INSPECTION ENTRY DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>
            Inspection Entry
          </Typography>

          {selected && (
            <Typography variant="body2" color="text.secondary">
              Coach: {selected.coach_no} | Visit: {selected.visit_no}
            </Typography>
          )}

          <IconButton
            onClick={closeDialog}
            disabled={saving}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Pre-Inspection Date"
              name="pre_inspection_date"
              type="date"
              value={form.pre_inspection_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Post-Inspection Date"
              name="post_inspection_date"
              type="date"
              value={form.post_inspection_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: form.pre_inspection_date || undefined,
              }}
            />

            <TextField
              label="Work Order Number"
              name="work_order_no"
              value={form.work_order_no}
              onChange={handleChange}
              fullWidth
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              label="Remark"
              name="remark"
              value={form.remark}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeDialog}
            color="inherit"
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {saving ? 'Saving...' : 'Save Inspection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inspection;